import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
} from 'react';
import { useSessionStorage } from '@uidotdev/usehooks';
import {
  USER_COUNTRY,
  USER_EMAIL,
  USER_NAME,
} from '@assetron/web-common/constants/LocalStorageConstants';
import { EMPTY_STRING } from '../constants/constant';

export interface SignupDataContextProps {
  userName: string;
  userEmail: string;
  userCountry: string;
  userNewPassword: string;
  setUserName: Dispatch<SetStateAction<string>>;
  setUserEmail: Dispatch<SetStateAction<string>>;
  setUserCountry: Dispatch<SetStateAction<string>>;
  setUserNewPassword: Dispatch<SetStateAction<string>>;
  clearInfo: () => void;
}

export const SignupDataContext = createContext<
  SignupDataContextProps | undefined
>(undefined);

export function SignupDataProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const [userName, setUserName] = useSessionStorage(USER_NAME, EMPTY_STRING);
  const [userEmail, setUserEmail] = useSessionStorage(USER_EMAIL, EMPTY_STRING);
  const [userCountry, setUserCountry] = useSessionStorage(
    USER_COUNTRY,
    EMPTY_STRING,
  );

  const [userNewPassword, setUserNewPassword] = React.useState(EMPTY_STRING);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const clearInfo = () => {
    setUserName(EMPTY_STRING);
    setUserEmail(EMPTY_STRING);
    setUserCountry(EMPTY_STRING);
    setUserNewPassword(EMPTY_STRING);
    window.sessionStorage.removeItem(USER_NAME);
    window.sessionStorage.removeItem(USER_EMAIL);
    window.sessionStorage.removeItem(USER_COUNTRY);
  };

  useEffect(() => {
    return () => {
      clearInfo();
    };
    // eslint forcing to add the dependency for clearInfo, But it goes in infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = useMemo(() => {
    return {
      userName,
      userEmail,
      userCountry,
      userNewPassword,
      setUserName,
      setUserEmail,
      setUserCountry,
      setUserNewPassword,
      clearInfo,
    };
  }, [
    userName,
    userEmail,
    userCountry,
    userNewPassword,
    setUserName,
    setUserEmail,
    setUserCountry,
    clearInfo,
  ]);

  return (
    <SignupDataContext.Provider value={contextValue}>
      {children}
    </SignupDataContext.Provider>
  );
}
